import React, { useState, useEffect } from 'react'
import styles from './CTAPopup.module.css'
import { notifyBrochureRequest } from '../../lib/whatsapp/sendWhatsApp'

const CTAPopup = ({
    isOpen,
    onClose,
    title = 'Request Floor Plan',
    subtitle = "Share your details and we'll send you the complete floor plan.",
}) => {
    const [form, setForm] = useState({ name: '', phone: '', email: '', consent: true })
    const [loading, setLoading] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose() }
        if (isOpen) document.addEventListener('keydown', handleKey)
        return () => document.removeEventListener('keydown', handleKey)
    }, [isOpen, onClose])

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : ''
        return () => { document.body.style.overflow = '' }
    }, [isOpen])

    // Reset state when popup re-opens
    useEffect(() => {
        if (isOpen) {
            setSubmitted(false)
            setError('')
            setForm({ name: '', phone: '', email: '', consent: true })
        }
    }, [isOpen])

    if (!isOpen) return null

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setLoading(true)
        setError('')

        try {
            // Determine request type from the popup title prop
            const requestType = title.toLowerCase().includes('brochure') ? 'Brochure' : 'Floor Plan'

            await notifyBrochureRequest({
                name: form.name,
                phone: form.phone,
                email: form.email,
                requestType,
            })

            setSubmitted(true)
        } catch (err) {
            console.error('WhatsApp notification failed:', err)
            setError('Something went wrong. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>

                {/* Close */}
                <button className={styles.close} onClick={onClose} aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>

                {/* Header */}
                <div className={styles.header}>
                    <span className={styles.eyebrow}>
                        <span className={styles.eyebrowLine}></span>
                        THE EVERETT
                        <span className={styles.eyebrowLine}></span>
                    </span>
                    <h2 className={styles.title}>{title}</h2>
                    <p className={styles.subtitle}>{subtitle}</p>
                </div>

                {submitted ? (
                    <div className={styles.successBox}>
                        <div className={styles.successIcon}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6L9 17l-5-5" />
                            </svg>
                        </div>
                        <h3 className={styles.successTitle}>Request Sent!</h3>
                        <p className={styles.successText}>
                            We've received your request and will be in touch shortly.
                        </p>
                    </div>
                ) : (
                    <form className={styles.form} onSubmit={handleSubmit}>
                        <div className={styles.inputGroup}>
                            <label className={styles.label}>Full Name *</label>
                            <input
                                className={styles.input}
                                type="text"
                                name="name"
                                placeholder="Your name"
                                value={form.name}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className={styles.inputGroup}>
                            <label className={styles.label}>Phone Number *</label>
                            <input
                                className={styles.input}
                                type="tel"
                                name="phone"
                                placeholder="+91 00000 00000"
                                value={form.phone}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className={styles.inputGroup}>
                            <label className={styles.label}>Email Address</label>
                            <input
                                className={styles.input}
                                type="email"
                                name="email"
                                placeholder="Optional"
                                value={form.email}
                                onChange={handleChange}
                            />
                        </div>

                        <label className={styles.consent}>
                            <input
                                type="checkbox"
                                name="consent"
                                checked={form.consent}
                                onChange={handleChange}
                                className={styles.checkbox}
                            />
                            <span>
                                I consent to the processing of provided data according to{' '}
                                <a href="#privacy">Privacy Policy</a> &amp; <a href="#terms">Terms &amp; Conditions</a>
                            </span>
                        </label>

                        {error && <p className={styles.errorText}>{error}</p>}

                        <button className={styles.submitBtn} type="submit" disabled={loading}>
                            {loading ? 'Sending...' : 'Send Me The Floor Plan'}
                            {!loading && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            )}
                        </button>
                    </form>
                )}

            </div>
        </div>
    )
}

export default CTAPopup